import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Pagination from '../../components/common/Pagination';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import { useAuth } from '../../context/AuthContext';
import {
  getAdminAccountList, getAdminAccount, createAdminAccount, updateAdminAccount, deactivateAdminAccount,
  changeAdminPassword, getAdminRoleList, createAdminRole, updateAdminRole,
} from '../../api/account';
import { getStoreList } from '../../api/store';
import { formatDateTime } from '../../utils/format';

const STORE_ADMIN_ROLE = 'STORE_ADMIN';

export default function AdminAccountListPage() {
  const [tab, setTab] = useState('accounts');

  return (
    <AdminLayout title="관리자계정관리" menuUrl="/admin/account">
      <div className="page-header">
        <div>
          <h1>관리자계정관리</h1>
          <div className="subtitle">관리자 계정과 권한(역할)을 관리합니다.</div>
        </div>
      </div>

      <div className="search-bar" style={{ marginBottom: 24 }}>
        <button className={`btn btn-sm ${tab === 'accounts' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('accounts')}>계정 목록</button>
        <button className={`btn btn-sm ${tab === 'roles' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTab('roles')}>권한(역할) 목록</button>
      </div>

      {tab === 'accounts' ? <AccountTab /> : <RoleTab />}
    </AdminLayout>
  );
}

function AccountTab() {
  const { auth } = useAuth();
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 10 });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [pwTarget, setPwTarget] = useState(null);
  const [roles, setRoles] = useState([]);

  const fetchList = useCallback(async (cond) => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminAccountList(cond);
      setPage(res.data);
    } catch (err) {
      setError(err.response?.data?.message || '관리자 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);
  useEffect(() => { getAdminRoleList().then((res) => setRoles(res.data || [])).catch(() => setRoles([])); }, []);

  const handleCreate = async (form) => {
    try {
      await createAdminAccount({ ...form, createdBy: auth.adminName });
      setCreating(false);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '관리자 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form) => {
    try {
      await updateAdminAccount(editing.adminId, { ...form, updatedBy: auth.adminName });
      setEditing(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '관리자 정보 수정에 실패했습니다.');
    }
  };

  const handleDeactivate = async () => {
    try {
      await deactivateAdminAccount(deactivateTarget.adminId, auth.adminName);
      setDeactivateTarget(null);
      fetchList(condition);
    } catch (err) {
      window.alert(err.response?.data?.message || '사용중지 처리에 실패했습니다.');
    }
  };

  const handleChangePassword = async (newPassword) => {
    try {
      await changeAdminPassword(pwTarget.adminId, newPassword);
      setPwTarget(null);
    } catch (err) {
      window.alert(err.response?.data?.message || '비밀번호 변경에 실패했습니다.');
    }
  };

  return (
    <>
      <div className="list-toolbar">
        <div className="list-toolbar-right">
          <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 관리자 추가</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>계정</th><th>이름</th><th>이메일</th><th>권한</th><th>사용여부</th><th>등록일</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={7} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={7} className="empty-row">등록된 관리자가 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((a) => (
                <tr key={a.adminId}>
                  <td>{a.adminAccount}</td>
                  <td>{a.adminName}</td>
                  <td>{a.adminEmail}</td>
                  <td><StatusBadge value={a.roleCode} /></td>
                  <td><StatusBadge value={a.useYn} /></td>
                  <td>{formatDateTime(a.createdAt)}</td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(a)}>수정</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setPwTarget(a)}>비밀번호</button>
                    {a.useYn === 'Y' && (
                      <button className="btn btn-danger btn-sm" onClick={() => setDeactivateTarget(a)}>사용중지</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {creating && <AccountFormModal roles={roles} onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <AccountFormModal roles={roles} account={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}

      {deactivateTarget && (
        <ConfirmDialog
          title="관리자 계정 사용중지"
          message={`${deactivateTarget.adminAccount} 계정을 사용중지 처리하시겠습니까?`}
          confirmLabel="사용중지"
          danger
          onCancel={() => setDeactivateTarget(null)}
          onConfirm={handleDeactivate}
        />
      )}

      {pwTarget && (
        <PasswordModal account={pwTarget} onClose={() => setPwTarget(null)} onSave={handleChangePassword} />
      )}
    </>
  );
}

function AccountFormModal({ account, roles, onClose, onSave }) {
  const isNew = !account;
  const [form, setForm] = useState({
    adminAccount: account?.adminAccount || '',
    adminPassword: '',
    adminName: account?.adminName || '',
    adminEmail: account?.adminEmail || '',
    roleCode: account?.roleCode || roles[0]?.roleCode || '',
    storeIds: account?.storeIds || [],
  });
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(!isNew);

  // 매장 목록은 STORE_ADMIN 선택 시에만 필요하지만, 역할을 바꿀 때 바로 보여줄 수 있도록 미리 로드해둔다.
  useEffect(() => {
    getStoreList().then((res) => setStores(res.data || [])).catch(() => setStores([]));
  }, []);

  // 목록화면에서 넘어온 account 객체에는 storeIds 가 없으므로(목록 조회 API가 내려주지 않음),
  // 수정 모달을 열 때 상세조회로 다시 가져와 채워준다 - 이게 없으면 기존에 배정된 매장이
  // 화면에 보이지 않아 "매장 지정이 안 되는 것처럼" 보인다.
  useEffect(() => {
    if (isNew || !account?.adminId) return;
    let cancelled = false;
    getAdminAccount(account.adminId)
      .then((res) => {
        if (cancelled) return;
        setForm((f) => ({ ...f, storeIds: res.data?.storeIds || [] }));
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingDetail(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, account?.adminId]);

  const isStoreAdmin = form.roleCode === STORE_ADMIN_ROLE;

  const toggleStore = (storeId) => {
    setForm((f) => {
      const has = f.storeIds.includes(storeId);
      return { ...f, storeIds: has ? f.storeIds.filter((id) => id !== storeId) : [...f.storeIds, storeId] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (isStoreAdmin && form.storeIds.length === 0) {
      window.alert('매장관리자는 배정할 매장을 하나 이상 선택해야 합니다.');
      return;
    }
    setSaving(true);
    try {
      // STORE_ADMIN 이 아닌 역할로 저장할 때는 매장 배정을 함께 비워서 보낸다 (서버에서도 역할이
      // 바뀌면 기존 매장 배정을 정리하지만, 값 자체를 명확히 비워 보내는 게 의도를 분명히 한다).
      await onSave({ ...form, storeIds: isStoreAdmin ? form.storeIds : [] });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isNew ? '관리자 추가' : '관리자 정보 수정'}
      onClose={onClose}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || loadingDetail}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>계정</label>
          <input className="input" value={form.adminAccount} disabled={!isNew}
            onChange={(e) => setForm({ ...form, adminAccount: e.target.value })} required />
        </div>
        {isNew && (
          <div className="field">
            <label>비밀번호</label>
            <input className="input" type="password" value={form.adminPassword}
              onChange={(e) => setForm({ ...form, adminPassword: e.target.value })} required />
          </div>
        )}
        <div className="field">
          <label>이름</label>
          <input className="input" value={form.adminName} onChange={(e) => setForm({ ...form, adminName: e.target.value })} required />
        </div>
        <div className="field">
          <label>이메일</label>
          <input className="input" value={form.adminEmail} onChange={(e) => setForm({ ...form, adminEmail: e.target.value })} />
        </div>
        <div className="field">
          <label>권한</label>
          <select className="select" value={form.roleCode} onChange={(e) => setForm({ ...form, roleCode: e.target.value })}>
            {roles.map((r) => <option key={r.roleCode} value={r.roleCode}>{r.roleName} ({r.roleCode})</option>)}
          </select>
        </div>
        {isStoreAdmin && (
          <div className="field">
            <label>배정 매장 (1개 이상 선택)</label>
            {loadingDetail && <div className="subtitle">배정 정보를 불러오는 중...</div>}
            {!loadingDetail && stores.length === 0 && <div className="subtitle">등록된 매장이 없습니다. 먼저 매장관리에서 매장을 등록하세요.</div>}
            {!loadingDetail && stores.length > 0 && (
              <div className="checkbox-list" style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4, padding: 8 }}>
                {stores.map((s) => (
                  <div key={s.storeId} className="field checkbox-row" style={{ marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      id={`store-${s.storeId}`}
                      checked={form.storeIds.includes(s.storeId)}
                      onChange={() => toggleStore(s.storeId)}
                    />
                    <label htmlFor={`store-${s.storeId}`} style={{ margin: 0 }}>{s.storeName}</label>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </form>
    </Modal>
  );
}

function PasswordModal({ account, onClose, onSave }) {
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(newPassword);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`${account.adminAccount} 비밀번호 변경`}
      onClose={onClose}
      width={360}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving || !newPassword}>{saving ? '변경 중...' : '변경'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>새 비밀번호</label>
          <input className="input" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
        </div>
      </form>
    </Modal>
  );
}

function RoleTab() {
  const { auth } = useAuth();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getAdminRoleList();
      setRoles(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || '역할 목록을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  const handleCreate = async (form) => {
    try {
      await createAdminRole({ ...form, createdBy: auth.adminName });
      setCreating(false);
      fetchRoles();
    } catch (err) {
      window.alert(err.response?.data?.message || '역할 등록에 실패했습니다.');
    }
  };

  const handleUpdate = async (form) => {
    try {
      await updateAdminRole(editing.roleCode, { ...form, updatedBy: auth.adminName });
      setEditing(null);
      fetchRoles();
    } catch (err) {
      window.alert(err.response?.data?.message || '역할 수정에 실패했습니다.');
    }
  };

  return (
    <>
      <div className="search-bar">
        <div style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setCreating(true)}>+ 역할 추가</button>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>역할코드</th><th>역할명</th><th>사용여부</th><th>관리</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={4} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={4} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && roles.length === 0 && <tr><td colSpan={4} className="empty-row">등록된 역할이 없습니다.</td></tr>}
              {!loading && !error && roles.map((r) => (
                <tr key={r.roleCode}>
                  <td>{r.roleCode}</td>
                  <td>{r.roleName}</td>
                  <td><StatusBadge value={r.useYn} /></td>
                  <td className="actions">
                    <button className="btn btn-secondary btn-sm" onClick={() => setEditing(r)}>수정</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && <RoleFormModal onClose={() => setCreating(false)} onSave={handleCreate} />}
      {editing && <RoleFormModal role={editing} onClose={() => setEditing(null)} onSave={handleUpdate} />}
    </>
  );
}

function RoleFormModal({ role, onClose, onSave }) {
  const isNew = !role;
  const [form, setForm] = useState({
    roleCode: role?.roleCode || '',
    roleName: role?.roleName || '',
    useYn: role?.useYn || 'Y',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={isNew ? '역할 추가' : '역할 수정'}
      onClose={onClose}
      width={360}
      footer={<>
        <button className="btn btn-secondary" onClick={onClose}>취소</button>
        <button className="btn btn-primary" onClick={submit} disabled={saving}>{saving ? '저장 중...' : '저장'}</button>
      </>}
    >
      <form onSubmit={submit}>
        <div className="field">
          <label>역할코드</label>
          <input className="input" value={form.roleCode} disabled={!isNew} onChange={(e) => setForm({ ...form, roleCode: e.target.value })} required />
        </div>
        <div className="field">
          <label>역할명</label>
          <input className="input" value={form.roleName} onChange={(e) => setForm({ ...form, roleName: e.target.value })} required />
        </div>
        <div className="field checkbox-row">
          <input type="checkbox" id="roleUseYn" checked={form.useYn === 'Y'} onChange={(e) => setForm({ ...form, useYn: e.target.checked ? 'Y' : 'N' })} />
          <label htmlFor="roleUseYn" style={{ margin: 0 }}>사용함</label>
        </div>
      </form>
    </Modal>
  );
}
